import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import Button from '@material-ui/core/Button';
import { ArrowLeft } from '@material-ui/icons';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import ConfirmTimeLayout from 'Components/Modals/ClientSchedulerModal/Default/ConfirmTime/ConfirmTimeLayout';
import { Typography } from '@material-ui/core';
import useResource from 'Hooks/useResource';
import {
  fetchDataFromLinkQueryParam,
  setNewLinkQueryParam,
  useClientSchedulerDispatchContext,
  useClientSchedulerStateContext,
} from 'Context/NewClientSchedulerContext';
import SkeletonOverlay from 'Components/UI/SkeletonOverlay';
import { ClientSchedulerCalendarEvent } from 'Overrides/ClientSchedulerCalendarEvent';
import { ClientSchedulerRescheduleCalendarEvent } from 'Overrides/ClientSchedulerRescheduleCalendarEvent';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import useMobileLayout from 'Hooks/useMobileLayout';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: 400,
    alignSelf: 'center',
    padding: theme.spacing(4),
  },
  metaFormRoot: {
    maxWidth: 400,
  },
  title: {
    color: theme.palette.facet.primaryBlue,
    fontWeight: theme.typography.fontWeightBold,
  },
  backButton: {
    color: theme.palette.facet.primaryBlue,
    fontWeight: theme.typography.fontWeightBold,
  },
  bookMeetingButton: {
    backgroundColor: theme.palette.facet.primaryBlue,
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.common.white,
    borderRadius: theme.radius.xl,
    flex: 1,
    width: '100%',
    padding: theme.spacing(2),
  },
});

const ConfirmTime = ({
  classes,
  className,
}) => {
  const {
    currentCollection,
    prevLink,
    selectedMeetingStart,
    selectedMeetingEnd,
    bufferDuration,
  } = useClientSchedulerStateContext();
  const dispatch = useClientSchedulerDispatchContext();
  const { logEventSuccess, logEventFailure, rootNodeProps } = useEventTrackingLogEvent();
  const mobileView = useMobileLayout();
  const [isLoading, setLoading] = useState(false);
  const {
    resource: calendarEventResource,
    initializeResource: setCalendarEventResource,
  } = useResource();

  const goToMonthlyView = () => {
    setNewLinkQueryParam(dispatch, prevLink);
    fetchDataFromLinkQueryParam(dispatch);
  };

  const bookMeetingAsClient = async () => {
    setLoading(true);
    dispatch({ type: 'loadingStart' });
    try {
      const [createdCalendarEventResource] = await calendarEventResource.submit();
      displaySuccess('Meeting saved');
      setNewLinkQueryParam(dispatch, createdCalendarEventResource.link('self').url());
      dispatch({ type: 'setMeetingBooked' });
      fetchDataFromLinkQueryParam(dispatch);
      logEventSuccess('Meeting saved from Client Scheduler', { mobileView });
    } catch (e) {
      // if the error is an array, use the detailed error message from the first error
      if (Array.isArray(e.errors)) {
        const [error] = e.errors;
        displayErrorMessage(e, error?.detail);
      } else {
        displayErrorMessage(e, e.errors);
      }
      logEventFailure('Meeting saved from Client Scheduler', { mobileView });
      setNewLinkQueryParam(dispatch, prevLink);
      fetchDataFromLinkQueryParam(dispatch);
    } finally {
      setLoading(false);
      dispatch({ type: 'loadingEnd' });
    }
  };


  useEffect(() => {
    let calendarEvent;
    if (currentCollection.resources().length() === 0) {
      calendarEvent = currentCollection.create({ metaOverrideSet: ClientSchedulerCalendarEvent });
      calendarEvent.attr('startTime', selectedMeetingStart);
      calendarEvent.attr('endTime', selectedMeetingEnd);
      calendarEvent.attr('bufferLength', bufferDuration);
      calendarEvent.attr('priority', '1');
    } else {
      calendarEvent = currentCollection.resources().head().overrideMeta(ClientSchedulerRescheduleCalendarEvent);
      calendarEvent.attr('startTime', selectedMeetingStart);
      calendarEvent.attr('bufferLength', bufferDuration);
    }
    setCalendarEventResource(calendarEvent);
  }, [currentCollection]);

  return (
    <div className={classNames(classes.root, className)} {...rootNodeProps}>
      <Typography variant="h2" className={classes.title}>
        Confirm Time
      </Typography>
      <Button
        className={classes.backButton}
        startIcon={<ArrowLeft />}
        onClick={goToMonthlyView}
      >
        Change Time
      </Button>
      <SkeletonOverlay loading={calendarEventResource}>
        <MetaFormInline
          classes={{
            root: classes.metaFormRoot,
          }}
          resource={calendarEventResource}
          layout={ConfirmTimeLayout}
          noIndividualSubmitButtons
        />
      </SkeletonOverlay>
      <Button
        className={classes.bookMeetingButton}
        variant="contained"
        disableElevation
        onClick={bookMeetingAsClient}
        disabled={isLoading}
        test="scheduler-new-meeting-book-meeting-button"
      >
        Book Meeting
      </Button>
    </div>
  );
};

ConfirmTime.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
};

ConfirmTime.defaultProps = {
  className: '',
};

export default withStyles(styles)(ConfirmTime);
